import React, { useContext, useEffect } from 'react';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment/moment';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import { Agent } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { getLanguageByCode } from '@moderepo/mode-app-components';
import { getPreferredLanguage } from '../../utils/utils';
import * as Constants from '../../utils/constants';
import { useGetCurrentProjectId } from '../../utils/customHooks';


/**
 * Fix for an issue which MomentUtils does not include suffice for the day in Japanese.
 * For example, May 4th is displayed as 5月4 instead of 5月4日
 */
class MomentUtilsFix extends MomentUtils {

    // Override the function that create the header text for the DATE picker
    getDatePickerHeaderText (date: moment.Moment): string {
        return new Intl.DateTimeFormat(this.locale, { month: 'short', day: '2-digit' }).format(date.toDate());
    }

    // Override the function that create the header text for the DATE and TIME picker
    getDateTimePickerHeaderText (date: moment.Moment): string {
        return new Intl.DateTimeFormat(this.locale, { month: 'short', day: '2-digit' }).format(date.toDate());
    }
}


/**
 * This is a simple component to wrap the app inside a DatePickerUtilsProvider.
 * All this component does is listen to the state's currentLanguage and currentProject change and update
 * DatePickerUtilsProvider current language. When DatePickerUtilsProvider current language is changed,
 * all the Date pickers will automatically be using the current language.
 */
export const DatePickerUtilsProvider: React.FC = ({ children }: React.PropsWithChildren<any>) => {
    const projectId = useGetCurrentProjectId(window.location.pathname);
    const { state:uiState } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const project = projectId ? selectProjectById(dataState, projectId) : undefined;
    const selectedLanguage = getLanguageByCode(
        Constants.SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || Constants.DEFAULT_LANGUAGE;
    const preferredLanguage = loggedInAgent ? getPreferredLanguage(project, loggedInAgent, Constants.DEFAULT_LANGUAGE) : selectedLanguage;

    useEffect(() => {
        try {
            // Dynamically load the locale for the current selected language
            require(`moment/locale/${preferredLanguage.code}`);
        } catch (error) {
            // Language not supported by moment, ignore the error
        }
        moment.locale(preferredLanguage.code);
    }, [preferredLanguage]);

    return (
        <MuiPickersUtilsProvider utils={MomentUtilsFix} locale={preferredLanguage.code}>
            {children}
        </MuiPickersUtilsProvider>
    );
};
